import { NgxSliderModule } from '@angular-slider/ngx-slider';
import { ObserversModule } from '@angular/cdk/observers';
import { CdkStepperModule } from '@angular/cdk/stepper';
import { CommonModule, DatePipe, DecimalPipe, NgOptimizedImage } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { BulkActionDialogComponent } from '@app/shared/components/bulk-actions/bulk-action-dialog/bulk-action-dialog.component';
import { SingleChoiceBulkActionHandlerComponent } from '@app/shared/components/bulk-actions/choice-bulk-action-handler/single-choice-bulk-action-handler.component';
import { MultiChoiceBulkActionHandlerComponent } from '@app/shared/components/bulk-actions/multi-bulk-action-handler/multi-choice-bulk-action-handler.component';
import { SimpleBulkActionHandlerComponent } from '@app/shared/components/bulk-actions/simple-bulk-action-handler/simple-bulk-action-handler.component';
import { CalendarComponent } from '@app/shared/components/calendar/calendar.component';
import { CheckColumnHeaderComponent } from '@app/shared/components/check-column-header/check-column-header.component';
import { CheckColumnComponent } from '@app/shared/components/check-column/check-column.component';
import {
  ClearInputComponent,
  ClearInputDirective,
} from '@app/shared/components/clear-input/clear-input.component';
import { DatatableFilterComponent } from '@app/shared/components/datatable-filter/datatable-filter.component';
import { DatatableOutletComponent } from '@app/shared/components/datatable-outlet/datatable-outlet.component';
import { DatatableSimplePaginationComponent } from '@app/shared/components/datatable-simple-pagination/datatable-simple-pagination.component';
import { DateRangePicker } from '@app/shared/components/date-range-picker/date-range-picker.component';
import { ErrorAlertComponent } from '@app/shared/components/error-alert/error-alert.component';
import { TargetComponent } from '@app/shared/components/feature-target/target.component';
import { FileDropAreaComponent } from '@app/shared/components/file-drop-area/file-drop-area.component';
import { FileComponent } from '@app/shared/components/file/file.component';
import { InfoDialogComponent } from '@app/shared/components/info-dialog/info-dialog.component';
import { LifecycleFlagSwitcherComponent } from '@app/shared/components/lifecycle-flag-switcher/lifecycle-flag-switcher.component';
import { LoadingIndicatorComponent } from '@app/shared/components/loading-indicator/loading-indicator.component';
import { OverlaySpinnerComponent } from '@app/shared/components/overlay-spinner/overlay-spinner.component';
import { PortalCardComponent } from '@app/shared/components/portal-card/portal-card.component';
import { SipCredentialsComponent } from '@app/shared/components/sip-credentials/sip-credentials.component';
import { UpdateContactGroupComponent } from '@app/shared/components/update-contact-group/update-contact-group.component';
import { DataTableDirective } from '@app/shared/directives/data-table.directive';
import { DelayedIfDirective } from '@app/shared/directives/delayed-if.directive';
import { HasAnyEnterpriseOptionDirective } from '@app/shared/directives/has-any-enterprise-option.directive';
import { HasEnterpriseOptionDirective } from '@app/shared/directives/has-enterprise-option.directive';
import { IfLevelDirective } from '@app/shared/directives/if-level.directive';
import { MenuLevelIsSystemDirective } from '@app/shared/directives/menu-level-is-system.directive';
import { TableCellDirective } from '@app/shared/directives/table-cell.directive';
import { TippyItemDirective } from '@app/shared/directives/tippy-item.directive';
import { TransclusionDirective } from '@app/shared/directives/transclusion.directive';
import { ValidTimeDirective } from '@app/shared/directives/valid-time.directive';
import { HighlightPipe } from '@app/shared/pipes/highlight.pipe';
import { NotBlankPipe } from '@app/shared/pipes/not-blank.pipe';
import { SipUriUserPartPipe } from '@app/shared/pipes/sip-uri-user-part.pipe';
import { PortalTranslatePipe } from '@app/shared/pipes/translate.pipe';
import { PortalNgbDatepickerI18n } from '@app/shared/services/ngb-datepicker-i18n';
import { NgbDatepickerI18n, NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { NgSelectModule } from '@ng-select/ng-select';
import { TranslateModule } from '@ngx-translate/core';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { AgendaComponent } from './components/agenda/agenda.component';
import { AnnouncementSelectComponent } from './components/announcement-select/announcement-select.component';
import { AuthUserInfoDialogComponent } from './components/auth-user-info-dialog/auth-user-info-dialog.component';
import { BreadcrumbPortalComponent } from './components/breadcrumb-portal/breadcrumb-portal.component';
import { BulkActionDialogFooterComponent } from './components/bulk-actions/bulk-action-dialog-footer/bulk-action-dialog-footer.component';
import { BulkActionDialogHeaderComponent } from './components/bulk-actions/bulk-action-dialog-header/bulk-action-dialog-header.component';
import { BulkActionEntriesComponent } from './components/bulk-actions/bulk-action-entries/bulk-action-entries.component';
import { BulkActionToolbarGroupComponent } from './components/bulk-actions/bulk-action-toolbar-group/bulk-action-toolbar-group.component';
import { BulkActionToolbarHeaderComponent } from './components/bulk-actions/bulk-action-toolbar-header/bulk-action-toolbar-header.component';
import { BulkActionToolbarItemComponent } from './components/bulk-actions/bulk-action-toolbar-item/bulk-action-toolbar-item.component';
import { BulkActionToolbarComponent } from './components/bulk-actions/bulk-action-toolbar/bulk-action-toolbar.component';
import { BulkActionComponent } from './components/bulk-actions/bulk-action/bulk-action.component';
import { ComparisonComponent } from './components/comparison/comparison.component';
import { ContactGroupSelectComponent } from './components/contact-group-select/contact-group-select.component';
import { ContactGroupsComponent } from './components/contact-groups/contact-groups.component';
import { ContactsExportImportComponent } from './components/contacts-export-import/contacts-export-import.component';
import { ContactsComponent } from './components/contacts/contacts.component';
import { CopyToClipboardComponent } from './components/copy-to-clipboard/copy-to-clipboard.component';
import { DatatableOptionalColumnDropdownComponent } from './components/datatable-optional-column-dropdown/datatable-optional-column-dropdown.component';
import { DatatablePaginationComponent } from './components/datatable-pagination/datatable-pagination.component';
import { DatePickerComponent } from './components/date-picker/date-picker.component';
import { DefaultBulkActionEntryComponent } from './components/default-bulk-action-entry/default-bulk-action-entry.component';
import { DropdownMenuComponent } from './components/dropdown-menu/dropdown-menu.component';
import { ElementsCountComponent } from './components/elements-count/elements-count.component';
import { ElementsSelectorItemComponent } from './components/elements-selector-item/elements-selector-item.component';
import { ElementsSelectorComponent } from './components/elements-selector/elements-selector.component';
import { ErrorBodyComponent } from './components/error-body/error-body.component';
import { ErrorDialogComponent } from './components/error-dialog/error-dialog.component';
import { ErrorHeaderComponent } from './components/error-header/error-header.component';
import { ErrorsComponent } from './components/errors/errors.component';
import { EvaluatedNumberInputComponent } from './components/evaluated-number-input/evaluated-number-input.component';
import { FileDownloadComponent } from './components/file-download/file-download.component';
import { FileDropComponent } from './components/file-drop/file-drop.component';
import { FileRejectedDescriptionComponent } from './components/file-rejected-description/file-rejected-description.component';
import { FileRejectedDialogComponent } from './components/file-rejected-dialog/file-rejected-dialog.component';
import { FileSelectComponent } from './components/file-select/file-select.component';
import { FileUploadDownloadComponent } from './components/file-upload-download/file-upload-download.component';
import { FilterInputComponent } from './components/filter-input/filter-input.component';
import { FormValidationErrorsComponent } from './components/form-validation-errors/form-validation-errors.component';
import { IconButtonComponent } from './components/icon-button/icon-button.component';
import { InheritableInputComponent } from './components/inheritable-input/inheritable-input.component';
import { LinkComponent } from './components/link/link.component';
import { LoadingIndicatorContainerComponent } from './components/loading-indicator-container/loading-indicator-container.component';
import { MenuLoadingIndicatorComponent } from './components/menu-loading-indicator/menu-loading-indicator.component';
import { ModalBodyComponent } from './components/modal-body/modal-body.component';
import { ModalFooterComponent } from './components/modal-footer/modal-footer.component';
import { ModalHeaderComponent } from './components/modal-header/modal-header.component';
import { NgSelectLabelBadgeComponent } from './components/ng-select-label-badge/ng-select-label-badge.component';
import { PageHeadingPostfixDirective } from './components/page-heading/page-heading-postfix.directive';
import { PageHeadingComponent } from './components/page-heading/page-heading.component';
import { PagedListSorterComponent } from './components/paged-list-sorter/paged-list-sorter.component';
import { PaginationComponent } from './components/pagination/pagination.component';
import { PasswordStrengthComponent } from './components/password-strength/password-strength.component';
import { PasswordToTextComponent } from './components/password-to-text/password-to-text.component';
import { PhoneNumbersComponent } from './components/phone-numbers/phone-numbers.component';
import { PlayFileComponent } from './components/play-file/play-file.component';
import { ProgressButtonComponent } from './components/progress-button/progress-button.component';
import { QuickJumpDialogComponent } from './components/quick-jump-dialog/quick-jump-dialog.component';
import { ReturnToLinkComponent } from './components/return-to-link/return-to-link.component';
import { RoutingGroupsSelectComponent } from './components/routing-groups-select/routing-groups-select.component';
import { SaveButtonComponent } from './components/save-button/save-button.component';
import { SkeletonComponent } from './components/skeleton/skeleton.component';
import { StepperComponent } from './components/stepper/stepper.component';
import { SwitchToggleComponent } from './components/switch-toggle/switch-toggle.component';
import { TimeTableSelectComponent } from './components/time-table-select/time-table-select.component';
import { ToggleableInputComponent } from './components/toggleable-input/toggleable-input.component';
import { UnsavedChangesBarComponent } from './components/unsaved-changes-bar/unsaved-changes-bar.component';
import { UpdateContactComponent } from './components/update-contact/update-contact.component';
import { UpdatePhoneNumberComponent } from './components/update-phone-number/update-phone-number.component';
import { ValidationErrorsComponent } from './components/validation-errors/validation-errors.component';
import { ViewTypeSwitcherComponent } from './components/view-type-switcher/view-type-switcher.component';
import { VpbxSwitcherComponent } from './components/vpbx-switcher/vpbx-switcher.component';
import { ConfirmationDialogComponent } from './dialog/confirmation-dialog/confirmation-dialog.component';
import { AutofocusDirective } from './directives/autofocus.directive';
import { DataTableFilterDirective } from './directives/data-table-filter.directive';
import { DropdownItemDirective } from './directives/dropdown-item.directive';
import { ForDirective } from './directives/for.directive';
import { HasRoleDirective } from './directives/has-role.directive';
import { InputValidityStateDirective } from './directives/input-validity-state.directive';
import { IsAuthorizedDirective } from './directives/is-authorized.directive';
import { MenuLevelIsEndUserDirective } from './directives/menu-level-is-end-user.directive';
import { MenuLevelIsEnterpriseDirective } from './directives/menu-level-is-enterprise.directive';
import { MenuLevelIsServiceProviderDirective } from './directives/menu-level-is-service-provider.directive';
import { RequiredC5VersionDirective } from './directives/required-c5-version.directive';
import { TippyDirective } from './directives/tippy.directive';
import { TranscludeDirective } from './directives/transclude.directive';
import { UserIsEndUserDirective } from './directives/user-is-end-user.directive';
import { UserIsEnterpriseDirective } from './directives/user-is-enterprise.directive';
import { UserIsServiceProviderDirective } from './directives/user-is-service-provider.directive';
import { ValidationErrorDirective } from './directives/validation-error.directive';
import { ViewportBasedClassDirective } from './directives/viewport-based-class.directive';
import { AccountStatePipe } from './pipes/account-state.pipe';
import { ContactNamePipe } from './pipes/contact-name.pipe';
import { DeviceNamePipe } from './pipes/device-name';
import { DurationPipe } from './pipes/duration.pipe';
import { FormatAnnouncementPipe } from './pipes/format-announcement.pipe';
import { FormatBytesPipe } from './pipes/format-bytes.pipe';
import { IsBlankPipe } from './pipes/is-blank.pipe';
import { NumberE164Pipe } from './pipes/number-e164.pipe';
import { FormatNumberRangePipe } from './pipes/number-range-format.pipe';
import { PhoneNumberPipe } from './pipes/phone-number.pipe';
import { PortalDatePipe } from './pipes/portal-date.pipe';
import { RoutingGroupNamePipe } from './pipes/routing-group-name.pipe';
import { StripUriPipe } from './pipes/strip-uri.pipe';
import { TrunkNamePipe } from './pipes/trunk-name.pipe';
import { UnderscoreToSpacePipe } from './pipes/underscore-to-space.pipe';
import { UserRolePipe } from './pipes/user-role.pipe';
import { UserPipe } from './pipes/user.pipe';

const components = [
  AgendaComponent,
  AutofocusDirective,
  CalendarComponent,
  ConfirmationDialogComponent,
  ContactNamePipe,
  DatatableOutletComponent,
  DatatablePaginationComponent,
  DatatableSimplePaginationComponent,
  DatePickerComponent,
  DateRangePicker,
  ErrorAlertComponent,
  ForDirective,
  FormValidationErrorsComponent,
  HasEnterpriseOptionDirective,
  HasRoleDirective,
  HighlightPipe,
  InfoDialogComponent,
  InputValidityStateDirective,
  IfLevelDirective,
  LoadingIndicatorComponent,
  MenuLevelIsSystemDirective,
  MenuLevelIsServiceProviderDirective,
  MenuLevelIsEnterpriseDirective,
  MenuLevelIsEndUserDirective,
  PortalCardComponent,
  SaveButtonComponent,
  SwitchToggleComponent,
  TableCellDirective,
  UnsavedChangesBarComponent,
  UserIsEndUserDirective,
  UserIsEnterpriseDirective,
  UserIsServiceProviderDirective,
  VpbxSwitcherComponent,
  FileDownloadComponent,
  ProgressButtonComponent,
  DatatableFilterComponent,
  RoutingGroupsSelectComponent,
  PasswordToTextComponent,
  SipCredentialsComponent,
];

@NgModule({
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    NgbModule,
    ReactiveFormsModule,
    NgSelectModule,
    TranslateModule,
    CdkStepperModule,
    NgxSliderModule,
    NgxSkeletonLoaderModule,
    ObserversModule,
    NgOptimizedImage,
  ],
  exports: [
    CommonModule,
    RouterModule,
    FormsModule,
    ReactiveFormsModule,
    NgbModule,
    NgSelectModule,
    TranslateModule,
    NgxSkeletonLoaderModule,
    ...components,
    LinkComponent,
    PortalTranslatePipe,
    ModalFooterComponent,
    ModalHeaderComponent,
    ModalBodyComponent,
    TimeTableSelectComponent,
    NumberE164Pipe,
    FormatNumberRangePipe,
    PhoneNumberPipe,
    FileSelectComponent,
    IconButtonComponent,
    UserRolePipe,
    AccountStatePipe,
    PasswordStrengthComponent,
    RoutingGroupNamePipe,
    DeviceNamePipe,
    BulkActionComponent,
    StepperComponent,
    PaginationComponent,
    TargetComponent,
    UserPipe,
    PlayFileComponent,
    DropdownMenuComponent,
    LoadingIndicatorContainerComponent,
    PortalDatePipe,
    FilterInputComponent,
    PagedListSorterComponent,
    PhoneNumbersComponent,
    UpdatePhoneNumberComponent,
    SipUriUserPartPipe,
    UnderscoreToSpacePipe,
    TippyDirective,
    ValidationErrorsComponent,
    ElementsSelectorComponent,
    ElementsCountComponent,
    ContactGroupsComponent,
    UpdateContactGroupComponent,
    FileUploadDownloadComponent,
    ValidationErrorDirective,
    DataTableFilterDirective,
    AnnouncementSelectComponent,
    ToggleableInputComponent,
    InheritableInputComponent,
    ContactsComponent,
    UpdateContactComponent,
    ContactGroupSelectComponent,
    ContactsExportImportComponent,
    ComparisonComponent,
    PageHeadingComponent,
    PageHeadingPostfixDirective,
    TranscludeDirective,
    ReturnToLinkComponent,
    MenuLoadingIndicatorComponent,
    DurationPipe,
    BreadcrumbPortalComponent,
    BreadcrumbPortalComponent,
    ViewTypeSwitcherComponent,
    TippyItemDirective,
    OverlaySpinnerComponent,
    CopyToClipboardComponent,
    DataTableDirective,
    CheckColumnComponent,
    CheckColumnHeaderComponent,
    ErrorsComponent,
    BulkActionDialogComponent,
    SimpleBulkActionHandlerComponent,
    SingleChoiceBulkActionHandlerComponent,
    BulkActionToolbarComponent,
    BulkActionToolbarItemComponent,
    BulkActionToolbarGroupComponent,
    BulkActionToolbarHeaderComponent,
    EvaluatedNumberInputComponent,
    TransclusionDirective,
    DatatableOptionalColumnDropdownComponent,
    DelayedIfDirective,
    StripUriPipe,
    RequiredC5VersionDirective,
    ValidTimeDirective,
    TrunkNamePipe,
    DropdownItemDirective,
    ViewportBasedClassDirective,
    HasAnyEnterpriseOptionDirective,
    IsAuthorizedDirective,
    NgSelectLabelBadgeComponent,
    FormatAnnouncementPipe,
    NotBlankPipe,
    IsBlankPipe,
    FileDropComponent,
    FileDropAreaComponent,
    LifecycleFlagSwitcherComponent,
    FormatBytesPipe,
    FileComponent,
    MultiChoiceBulkActionHandlerComponent,
    FileRejectedDescriptionComponent,
    ClearInputComponent,
    ClearInputDirective,
  ],
  declarations: [
    ...components,
    LinkComponent,
    PortalTranslatePipe,
    ModalFooterComponent,
    ModalHeaderComponent,
    ModalBodyComponent,
    TimeTableSelectComponent,
    NumberE164Pipe,
    FormatNumberRangePipe,
    FileSelectComponent,
    PhoneNumberPipe,
    IconButtonComponent,
    UserRolePipe,
    AccountStatePipe,
    PasswordStrengthComponent,
    RoutingGroupNamePipe,
    BulkActionComponent,
    DefaultBulkActionEntryComponent,
    PaginationComponent,
    DeviceNamePipe,
    StepperComponent,
    TargetComponent,
    UserPipe,
    PlayFileComponent,
    DropdownMenuComponent,
    LoadingIndicatorContainerComponent,
    PortalDatePipe,
    FilterInputComponent,
    PagedListSorterComponent,
    PhoneNumbersComponent,
    UpdatePhoneNumberComponent,
    SipUriUserPartPipe,
    UnderscoreToSpacePipe,
    TippyDirective,
    ValidationErrorsComponent,
    ElementsSelectorComponent,
    ElementsSelectorItemComponent,
    ElementsCountComponent,
    ContactGroupsComponent,
    UpdateContactGroupComponent,
    FileUploadDownloadComponent,
    ValidationErrorDirective,
    DataTableFilterDirective,
    AnnouncementSelectComponent,
    ToggleableInputComponent,
    InheritableInputComponent,
    ContactsComponent,
    UpdateContactComponent,
    ContactGroupSelectComponent,
    ContactsExportImportComponent,
    ComparisonComponent,
    PageHeadingComponent,
    PageHeadingPostfixDirective,
    TranscludeDirective,
    ReturnToLinkComponent,
    MenuLoadingIndicatorComponent,
    DurationPipe,
    BreadcrumbPortalComponent,
    BreadcrumbPortalComponent,
    SkeletonComponent,
    ViewTypeSwitcherComponent,
    TippyItemDirective,
    OverlaySpinnerComponent,
    CopyToClipboardComponent,
    DataTableDirective,
    CheckColumnComponent,
    CheckColumnHeaderComponent,
    ErrorsComponent,
    BulkActionDialogComponent,
    BulkActionEntriesComponent,
    BulkActionDialogHeaderComponent,
    BulkActionDialogFooterComponent,
    SimpleBulkActionHandlerComponent,
    SingleChoiceBulkActionHandlerComponent,
    BulkActionToolbarComponent,
    BulkActionToolbarItemComponent,
    BulkActionToolbarGroupComponent,
    BulkActionToolbarHeaderComponent,
    EvaluatedNumberInputComponent,
    TransclusionDirective,
    DatatableOptionalColumnDropdownComponent,
    DelayedIfDirective,
    RoutingGroupsSelectComponent,
    StripUriPipe,
    ErrorDialogComponent,
    ErrorHeaderComponent,
    ErrorBodyComponent,
    RequiredC5VersionDirective,
    ValidTimeDirective,
    TrunkNamePipe,
    DropdownItemDirective,
    ViewportBasedClassDirective,
    HasAnyEnterpriseOptionDirective,
    IsAuthorizedDirective,
    NgSelectLabelBadgeComponent,
    FormatAnnouncementPipe,
    NotBlankPipe,
    FileDropComponent,
    FileRejectedDialogComponent,
    FileRejectedDescriptionComponent,
    FormatBytesPipe,
    FileDropAreaComponent,
    LifecycleFlagSwitcherComponent,
    IsBlankPipe,
    FileComponent,
    AuthUserInfoDialogComponent,
    MultiChoiceBulkActionHandlerComponent,
    QuickJumpDialogComponent,
    ClearInputComponent,
    ClearInputDirective,
  ],
  providers: [
    ContactNamePipe,
    DatePipe,
    DecimalPipe,
    DurationPipe,
    PhoneNumberPipe,
    PortalDatePipe,
    UserPipe,
    DeviceNamePipe,
    TrunkNamePipe,
    FormatNumberRangePipe,
    FormatBytesPipe,
    {
      provide: NgbDatepickerI18n,
      useClass: PortalNgbDatepickerI18n,
    },
  ],
})
export class SharedModule {}
